import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form, Row, Col, Button, Badge, InputGroup } from 'react-bootstrap';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';
import moment from 'moment';
import s from './EditDriverForm.css';
import Loader from '../../Common/Loader/Loader';
import {
  setDriverPriorityOverride,
  clearDriverPriorityOverride
} from '../../../actions/siteadmin/driverPriorityOverride';
import { priorityPercent, EARLY_COMPLETION_MIN_COMPLETIONS } from '../../../helpers/driverPriority';

const hasOverride = (acceptance) =>
  acceptance.overridePercent !== null && acceptance.overridePercent !== undefined;

// The priority dispatch ranks the driver by now, override included.
const currentPercent = (acceptance) =>
  hasOverride(acceptance) ? acceptance.overridePercent : priorityPercent(acceptance.priorityFactor || 1);

const initialState = (acceptance) => ({
  percent: String(currentPercent(acceptance)),
  reason: hasOverride(acceptance) ? (acceptance.overrideReason || '') : '',
  error: null
});

// Lets an admin set the priority food/grocery dispatch ranks this driver by,
// replacing both the acceptance and early-completion penalties. The override
// expires with the stats window.
class DriverPriority extends Component {
  constructor(props) {
    super(props);
    this.state = initialState(props.acceptance);
  }

  // Refetch after a save or removal brings a new current value.
  componentDidUpdate(prevProps) {
    const prev = prevProps.acceptance;
    const next = this.props.acceptance;
    if (
      prev.overridePercent !== next.overridePercent ||
      prev.overrideExpiresAt !== next.overrideExpiresAt ||
      prev.priorityFactor !== next.priorityFactor
    ) {
      this.setState(initialState(next));
    }
  }

  // Digits only, capped at 100; `max` alone does not stop typed input.
  handlePercentChange = (event) => {
    const digits = event.target.value.replace(/\D/g, '');
    const percent = digits === '' ? '' : String(Math.min(100, Number(digits)));
    this.setState({ percent, error: null });
  };

  handleApply = (event) => {
    event.preventDefault();
    const { driverId, profileId, setDriverPriorityOverride } = this.props;
    const percent = Number(this.state.percent);

    if (this.state.percent === '' || !Number.isInteger(percent) || percent < 1 || percent > 100) {
      this.setState({ error: 'Enter a number from 1 to 100.' });
      return;
    }

    this.setState({ error: null });
    setDriverPriorityOverride(driverId, percent, this.state.reason.trim(), profileId);
  };

  handleClear = () => {
    const { driverId, profileId, clearDriverPriorityOverride } = this.props;
    clearDriverPriorityOverride(driverId, profileId);
  };

  renderStatus(acceptance) {
    const factor = acceptance.priorityFactor || 1;
    const priority = priorityPercent(factor);

    return (
      <div className={s.space3}>
        <div className='mb-2'>
          {`Dispatch priority `}
          <Badge pill variant={priority >= 100 ? 'success' : priority >= 50 ? 'warning' : 'danger'}>
            {`${priority}%`}
          </Badge>
          <small className='text-muted'>
            {factor > 1 ? ` ranked as if ${factor.toFixed(2)}x farther away` : ' normal'}
            {hasOverride(acceptance) && ' (set manually)'}
          </small>
        </div>
        <ul className='mb-0 pl-3'>
          <li>
            {!(acceptance.offers > 0)
              ? <span className='text-muted'>{`No orders offered in the last ${acceptance.windowDays} days.`}</span>
              : (
                <span>
                  {`Accepted `}<strong>{`${acceptance.accepted} of ${acceptance.offers}`}</strong>{` orders offered in the last ${acceptance.windowDays} days`}
                  {acceptance.noResponse > 0 && (
                    <small className='text-muted'>{` (${acceptance.noResponse} not answered, counted as half a refusal)`}</small>
                  )}
                </span>
              )}
          </li>
          {acceptance.earlyCompletions > 0 && (
            <li>
              {`Marked delivered away from the drop-off: `}<strong>{`${acceptance.earlyCompletions} of ${acceptance.completions}`}</strong>
              <small className='text-muted'>
                {` (more than ${acceptance.earlyCompletionThresholdMeters} m away${acceptance.completions < EARLY_COMPLETION_MIN_COMPLETIONS ? `; counts once there are ${EARLY_COMPLETION_MIN_COMPLETIONS} orders` : ''})`}
              </small>
            </li>
          )}
        </ul>
      </div>
    );
  }

  render() {
    const { acceptance, loading } = this.props;
    const { percent, reason, error } = this.state;

    if (!acceptance) return null;

    const overridden = hasOverride(acceptance);

    return (
      <div>
        {this.renderStatus(acceptance)}

        {overridden && (
          <div className={cx('alert alert-info', s.space3)}>
            <strong>{`Priority set manually to ${acceptance.overridePercent}%`}</strong>
            {` until ${moment(acceptance.overrideExpiresAt).format('D MMM YYYY, HH:mm')}`}
            {acceptance.overrideReason && <div className='small'>{`Reason: ${acceptance.overrideReason}`}</div>}
          </div>
        )}

        <Form onSubmit={this.handleApply}>
          <Row>
            <Col lg={4} md={12} sm={12} xs={12}>
              <div className={cx('inputFormAddon', 'addonBorder')}>
                <Form.Group className={s.space3}>
                  <InputGroup>
                    <Form.Label className='inputLabel'>Set priority</Form.Label>
                    <Form.Control
                      type='number'
                      min={1}
                      max={100}
                      step={1}
                      value={percent}
                      onChange={this.handlePercentChange}
                      className={s.formControlInput}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                  {error && <span className={cx(s.errorMessage, 'errorMessageRTL')}>{error}</span>}
                </Form.Group>
              </div>
            </Col>
            <Col lg={8} md={12} sm={12} xs={12}>
              <Form.Group className={cx(s.space3, 'positionRelative')}>
                <Form.Label className='inputLabel'>Reason</Form.Label>
                <Form.Control
                  type='text'
                  maxLength={255}
                  value={reason}
                  onChange={e => this.setState({ reason: e.target.value })}
                  placeholder='e.g. phone was broken last week'
                  className={s.formControlInput}
                />
              </Form.Group>
            </Col>
          </Row>

          <div className='d-flex flex-wrap align-items-center justify-content-between' style={{ gap: '1rem' }}>
            <small className='text-muted'>
              {`Replaces both the acceptance and early-completion penalties. 100% is normal priority; 50% ranks the driver as if twice as far. Goes back to automatic after ${acceptance.windowDays} days.`}
            </small>
            <div className='d-flex align-items-center' style={{ gap: '0.5rem' }}>
              {overridden && (
                <Button
                  variant='light'
                  onClick={this.handleClear}
                  disabled={loading}
                  style={{ padding: '6px 30px', fontSize: '18px' }}
                >
                  Back to automatic
                </Button>
              )}
              <Loader
                type='button'
                label='Save'
                show={loading}
                buttonType='submit'
                className={cx(s.button, s.btnPrimary)}
                disabled={loading}
                isSuffix={true}
              />
            </div>
          </div>
        </Form>
      </div>
    );
  }
}

const mapState = state => ({
  loading: state.loader.DriverPriorityOverride
});

const mapDispatch = {
  setDriverPriorityOverride,
  clearDriverPriorityOverride
};

export default withStyles(s)(connect(mapState, mapDispatch)(DriverPriority));
